import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiShield } from "react-icons/fi";
import api from "../../services/api";

const VirtualCard = () => {
  const navigate = useNavigate();
  const [cardData, setCardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const fetchVirtualCard = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/virtual-card", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load virtual card.");
          return;
        }

        setCardData(response.data?.data || null);
      } catch (error) {
        setLoadError("Failed to load virtual card.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchVirtualCard();
  }, []);

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isExpired = (value) => {
    if (!value) {
      return false;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    return date.getTime() < Date.now();
  };

  const isConsumed = (card) => {
    if (!card) {
      return false;
    }
    if (Number(card.status_id) === 5) {
      return true;
    }
    const statusName = String(card.status_name || "").toLowerCase();
    return statusName === "consumed";
  };

  const expired = isExpired(cardData?.expiration_date);
  const consumed = isConsumed(cardData);
  const canRenew = expired || consumed;
  const statusLabel = cardData
    ? consumed
      ? "Consumed"
      : expired
        ? "Expired"
        : "Active"
    : "No Card";

  return (
    <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
            Medical Benefits
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-slate-900">
            Virtual Card
          </h2>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            cardData
              ? consumed
                ? "bg-amber-100 text-amber-700"
                : expired
                  ? "bg-rose-100 text-rose-600"
                  : "bg-emerald-100 text-emerald-600"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          {statusLabel}
        </span>
      </div>
      {loadError && (
        <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {loadError}
        </div>
      )}
      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="w-full max-w-[320px]">
          <div className="mb-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-500">
            Amaphil ID: <span className="text-slate-800">{cardData?.amaphil_id || "-"}</span>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl transition hover:-translate-y-1 hover:shadow-2xl">
            {cardData?.product_image ? (
              <img
                src={cardData.product_image}
                alt={cardData.title || "Virtual card"}
                className="h-56 w-full object-contain p-4"
              />
            ) : (
              <div className="flex h-56 items-center justify-center bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
                No Card Image
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <FiShield className="text-emerald-500" />
            Card status verified
          </div>
          <p className="mt-3 text-sm text-slate-500">
            Expires: {formatDate(cardData?.expiration_date)}
          </p>
          {consumed && (
            <p className="mt-2 text-sm text-slate-500">
              Consumed: {formatDate(cardData?.consumed_at)}
            </p>
          )}
          <button
            type="button"
            onClick={() => navigate("/products")}
            className={`mt-6 inline-flex items-center justify-center rounded-xl px-4 py-2 text-xs font-semibold uppercase tracking-wide transition ${
              canRenew
                ? "bg-[var(--brand-orange)] text-white shadow-lg shadow-orange-200 hover:bg-[var(--brand-orange-dark)]"
                : "bg-slate-200 text-slate-500"
            }`}
            disabled={!canRenew}
          >
            Renew
          </button>
        </div>
      </div>
    </div>
  );
};

export default VirtualCard;
